import { useState } from 'react';
import { Text, Flex, Box, useDisclosure } from '@chakra-ui/react';
import {
  ButtonText,
  Accordion,
  Badge,
  Button,
  useToast,
  AccordionProps,
  Banner
} from '@morressier/alembic';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faFileExport,
  faPlus,
  faMinus
} from '@fortawesome/pro-regular-svg-icons';
import EditorReview from './EditorReview';
import AuthorReview from './AuthorReview';
import AboutReview from './AboutReview';
import { formatDate } from 'src/utils/date';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import {
  IPeerReviewListItem,
  IPeerReviewListItemWithRescission
} from '@publishing-engine/domain/views/review';
import { useRateReview, useRescindFirstAssessment } from 'src/api/commands';
import { RATING_OPTIONS } from 'src/utils/reviews';
import { IFirstAssessmentRescission } from '@publishing-engine/domain/models';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import RescindReviewModal, {
  RescindReviewFormValues
} from 'src/components/Review/RescindReviewModal';
import RescindReview from './RescindedReview';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { useKeycloak } from 'src/components/AuthHOC';
import ProtectedReviewWorkflowAction from 'src/components/ProtectedAction/ProtectedReviewWorkflowAction';
import ReviewerOptIns from './ReviewerOptIns';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAdjudicationInviteDetails,
  fetchOrganizationAndJournalsInformation
} from 'src/api/queries';
import { Tooltip } from '@chakra-ui/tooltip';
import ReviewTimeline from './ReviewTimeline';
import RateReviewWithEmail from './RateReviewWithEmail';
import { IEmailCommandPayload } from '@publishing-engine/domain/types/email-template';

interface Props {
  review: IPeerReviewListItem;
  onBackToReviews: () => void;
  reviewFieldConfig: any;
}

const ReviewDetails: React.FC<Props> = ({
  review,
  onBackToReviews,
  reviewFieldConfig
}) => {
  const { isOpen, onToggle } = useDisclosure();

  const { availableReviewActions, organizationId, revisionHistory } =
    useSubmissionData();
  const { firstAssessment, revisionAssessment } = review.caseData;
  const reviewAssessment = revisionAssessment ?? firstAssessment;
  const submissionId = review.submission.id;
  const ctxId = useCurrentContextId() ?? '';
  const { currentUser } = useKeycloak();
  const firstReviewerFeedback = revisionHistory?.current?.rounds
    ?.flatMap(round => round?.reviewerRevisionFeedback ?? [])
    ?.find(
      feedback =>
        feedback?.reviewAssessment &&
        feedback.reviewer?.id === review?.reviewer?.id
    );

  const peerReviewTransparency =
    firstReviewerFeedback?.reviewAssessment?.peerReviewTransparency;
  const reviewerAnonymity =
    firstReviewerFeedback?.reviewAssessment?.reviewerAnonymity;
  const formattedDate = reviewAssessment?.date
    ? formatDate(new Date(reviewAssessment.date))
    : 'N/A';

  const { data: organizationData } = useQuery({
    queryKey: ['organization'],
    queryFn: () =>
      organizationId
        ? fetchOrganizationAndJournalsInformation(organizationId)
        : null
  });

  // state
  const [showRateReviewModal, setShowRateReviewModal] = useState(false);
  const [showRescindReviewModal, setShowRescindReviewModal] = useState(false);

  const toast = useToast();

  const {
    data: adjudicationInviteList,
    isLoading: isLoadingAdjudicationInvites
  } = useQuery({
    queryKey: [
      'adjudications-experts-invite-list',
      { submissionContextId: ctxId, submissionId }
    ],
    queryFn: () =>
      submissionId && ctxId
        ? fetchAdjudicationInviteDetails(ctxId, submissionId, 'adjudications')
        : null
  });

  const { mutateAsync: mutateRateReview } = useRateReview({
    submissionId,
    organizationId,
    reviewId: review.id,
    submissionContextId: ctxId,
    reviewerId: review.reviewer.id,
    queryKey: ['submission-context', ctxId, submissionId, 'reviews'] // fetch reviews query key
  });
  const { mutateAsync: mutateRescindReview } = useRescindFirstAssessment({
    submissionId,
    reviewId: review.id,
    organizationId,
    submissionContextId: ctxId,
    reviewerId: review.reviewer.id,
    queryKey: ['submission-context', ctxId, submissionId, 'reviews'] // fetch reviews query key
  });

  // TODO: We can introduce new synthetic variable for "in progress" adjudication, this way we can that in setup guard for rescind review
  // same guard we can use on frontend as well, so adjudicators can not submit the form.
  // ticket for that https://morressier.atlassian.net/browse/JEDIS-2431
  const enableRescindReview =
    availableReviewActions.contains(review, 'RescindFirstAssessment') &&
    !isLoadingAdjudicationInvites &&
    adjudicationInviteList?.inProgress === 0;

  const canRateReview = !!review.canRateReview;

  const renderHeader = () => {
    const isCoReviewed = review.coReview?.status === 'accepted';
    const coReviewerObj = review.coReview?.coReviewer;
    const coReviewer =
      coReviewerObj?.firstName && coReviewerObj?.lastName
        ? `${coReviewerObj.firstName} ${coReviewerObj.lastName}`
        : coReviewerObj?.email;
    const reviewedBy = `By ${review.reviewer.firstName} ${
      review.reviewer.lastName
    }${
      isCoReviewed && coReviewer ? ` & ${coReviewer}` : ''
    } on ${formattedDate}`;

    const reviewTimelineIcon = isOpen ? (
      <FontAwesomeIcon icon={faMinus} />
    ) : (
      <FontAwesomeIcon icon={faPlus} />
    );

    return (
      <>
        <ButtonText
          leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
          maxWidth="fit-content"
          onClick={onBackToReviews}
        >
          Back to Reviews
        </ButtonText>
        <Flex justifyContent="space-between" alignItems="center">
          <Flex gap={2} flexDirection="column">
            <Box>
              <Badge variant="neutral" size="context">
                Review
              </Badge>
            </Box>
            <Text textStyle="headingTitle">Assess this Review</Text>
            <Flex columnGap={3}>
              <Text>{reviewedBy}</Text>
              {review?.timeline && (
                <ButtonText maxWidth="fit-content" onClick={onToggle}>
                  {reviewTimelineIcon} Review Timeline
                </ButtonText>
              )}
            </Flex>
          </Flex>
          {canRateReview ||
          review.workflow.currentState === 'submittedFirstAssessment' ||
          review.workflow.currentState === 'submittedRevisionAssessment' ? (
            <Flex gap={4} justifyContent="center" alignItems="center">
              {canRateReview && (
                <ProtectedReviewWorkflowAction
                  actionName="RateReview"
                  reviewId={review.id}
                >
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowRateReviewModal(true);
                    }}
                    disabled={review.isUserDeactivated}
                  >
                    Rate Review
                  </Button>
                </ProtectedReviewWorkflowAction>
              )}
              {(review.workflow.currentState === 'submittedFirstAssessment' ||
                review.workflow.currentState ===
                  'submittedRevisionAssessment') && (
                <ProtectedReviewWorkflowAction
                  actionName="RescindFirstAssessment"
                  reviewId={review.id}
                >
                  <Tooltip
                    isDisabled={
                      isLoadingAdjudicationInvites || enableRescindReview
                    }
                    shouldWrapChildren
                    hasArrow
                    label={
                      'This review cannot be rescinded as it is currently under adjudication.'
                    }
                    fontSize="md"
                    placement="top"
                  >
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowRescindReviewModal(true);
                      }}
                      disabled={
                        review.isUserDeactivated || !enableRescindReview
                      }
                    >
                      Rescind Review
                    </Button>
                  </Tooltip>
                </ProtectedReviewWorkflowAction>
              )}
            </Flex>
          ) : null}
        </Flex>
        <Flex mt={-4} w="100%">
          {review?.timeline && (
            <ReviewTimeline
              isOpen={isOpen}
              reviewTimelineData={review.timeline!}
            />
          )}
        </Flex>
      </>
    );
  };

  const items = [
    {
      title: 'For Editors',
      subtitle:
        'Review the scores, feedback and recommendations sent to you by the reviewer.',
      content: organizationData && (
        <EditorReview
          reviewAssessment={
            review.caseData.revisionAssessment ??
            review.caseData.firstAssessment!
          }
          reviewFieldConfig={reviewFieldConfig}
          organizationData={organizationData}
          submissionId={ctxId}
        />
      )
    },
    {
      title: 'For Authors',
      subtitle:
        'Review and moderate the feedback left by reviewers for authors.',
      content: (
        <AuthorReview
          review={review}
          reviewFieldConfig={reviewFieldConfig}
          submissionId={ctxId}
        />
      )
    },
    {
      title: 'About Review',
      subtitle:
        "Rate this Review, and verify the reviewer's profile and preferences.",
      content: <AboutReview review={review} />
    }
  ] as AccordionProps['items'];

  // if WebOfScienceOptIn and ReceiveFeedback are present, add the opt-ins accordion
  const isWebOfScienceOptInAvailable =
    review && reviewFieldConfig?.webOfScienceOptIn;
  const isReceiveFeedbackAvailable =
    review && reviewFieldConfig?.ReceiveFeedback;

  if (isWebOfScienceOptInAvailable || isReceiveFeedbackAvailable) {
    items.splice(2, 0, {
      title: 'Opt-Ins',
      content: (
        <ReviewerOptIns
          review={review}
          peerReviewTransparency={peerReviewTransparency}
          reviewerAnonymity={reviewerAnonymity}
          reviewFieldConfig={reviewFieldConfig}
        />
      )
    });
  }
  if (
    review.caseData.firstAssessmentRescission &&
    review.workflow.currentState === 'rescinded'
  ) {
    const rescindFeedbackSentDate =
      review.workflow.states?.find(
        state => state.completedWith?.action === 'RescindedFirstAssessment'
      )?.completedWith?.date || '';

    items.unshift({
      title: 'Review Rescinded',
      subtitle: `This feedback was sent to the Reviewer on ${formatDate(
        new Date(rescindFeedbackSentDate)
      )}.`,
      content: (
        <RescindReview review={review as IPeerReviewListItemWithRescission} submissionContextId={ctxId} />
      ),
      icon: (
        <Box marginX={2}>
          <FontAwesomeIcon icon={faFileExport} size="lg" />
        </Box>
      )
    });
  }

  const handleRateReview = async (
    rating: string,
    justification?: string,
    emails?: IEmailCommandPayload
  ) => {
    const response = await mutateRateReview({
      reviewScore: { score: rating, justification },
      emails
    });
    if (response.isSuccess) {
      const ratingOption = RATING_OPTIONS[rating];
      toast({
        variant: 'success',
        title: `Review Rated as ${ratingOption?.value} (${ratingOption?.label})`,
        description:
          firstAssessment?.receiveFeedback === 'yes'
            ? 'The Reviewer will receive the results once a decision for this submission is made.'
            : ''
      });
      setShowRateReviewModal(false);
    } else {
      toast({
        variant: 'error',
        title: 'Error Rating Review'
      });
    }
  };

  const handleRescindReview = async (formValues: RescindReviewFormValues) => {
    const rescindReview: IFirstAssessmentRescission = {
      feedback: formValues.feedback,
      attachments: formValues.attachments,

      actionBy: {
        userId: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName
      }
    };

    const response = await mutateRescindReview({
      rescindReview
    });
    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Review Rescinded',
        description: `${review.reviewer.firstName} ${review.reviewer.lastName} will receive your feedback by email, which will be recorded in their review.`,
        isClosable: true
      });

      setShowRescindReviewModal(false);
    }
  };

  return (
    <>
      {showRateReviewModal && (
        <RateReviewWithEmail
          reviewId={review.id}
          reviewRequestId={review.reviewer.id}
          email={review.reviewer.email}
          onClose={() => setShowRateReviewModal(false)}
          onRateReview={handleRateReview}
          receiveFeedback={firstAssessment?.receiveFeedback || 'no'}
          organizationId={organizationId}
          submissionContextId={ctxId}
        />
      )}
      {showRescindReviewModal && (
        <RescindReviewModal
          isOpen={showRescindReviewModal}
          onClose={() => setShowRescindReviewModal(false)}
          reviewer={review.reviewer}
          submissionContextId={ctxId}
          submissionId={submissionId}
          onRescindReview={handleRescindReview}
        />
      )}
      <Overlay>
        <Flex flexDirection="column" backgroundColor="surfaceSecondaryFill">
          <Layout width="48rem">
            <Flex gap={8} flexDirection="column">
              {renderHeader()}

              {review.coReview?.status === 'accepted' && (
                <Banner
                  variant="attention"
                  heading="Co-Review Notice"
                  content={`This review was submitted with a Co-Reviewer: ${
                    review.coReview.coReviewer?.firstName || ''
                  } ${
                    review.coReview.coReviewer?.lastName ||
                    review.coReview.coReviewer?.email ||
                    ''
                  }. Both Reviewers will be notified of any changes or scores, and this Review will be credited to both accounts.`}
                />
              )}

              <Accordion
                chakraAccordionProps={{
                  defaultIndex: [0]
                }}
                accordionItemProps={{
                  background: 'white' // TODO: How to replace with whiteSecondaryFill?
                }}
                items={items}
              />
            </Flex>
          </Layout>
        </Flex>
      </Overlay>
    </>
  );
};

export default ReviewDetails;
